<template>
  <q-card
    class="movie-card cursor-pointer bg-responsive-white"
    :class="{ 'col q-mx-auto': $isLandscapeView() && $q.screen.gt.xs }"
    :style="{ 'max-width': $isLandscapeView() && $q.screen.gt.xs ? '350px' : undefined }"
    flat
    bordered
  >
    <component
      :is="!$isLandscapeView() || $q.screen.lt.sm ? 'q-card-section' : 'div'"
      :horizontal="!$isLandscapeView() || $q.screen.lt.sm"
    >
      <q-img
        :class="{
          'poster': !$isLandscapeView() || $q.screen.lt.sm,
          'col-2': !$isLandscapeView() && $q.screen.lt.sm,
          'col-1': !$isLandscapeView() && $q.screen.gt.xs
        }"
        :src="$isLandscapeView() && $q.screen.lt.md ? movie.bannerImageUrl : movie.posterImageUrl"
        :alt="movie.name"
        @click.stop="selectMovie(movie)"
      />
      <q-card-section
        :class="{
          'q-pa-none': $isLandscapeView(),
          'q-pb-sm q-pt-none q-px-none': !$isLandscapeView(),
          'col-10': !$isLandscapeView() && $q.screen.xs,
          'col-9': !$isLandscapeView() && $q.screen.gt.xs
        }"
      >
        <div
          class="column"
          :class="$q.screen.gt.md || $isLandscapeView() ? 'q-pa-md' : 'q-pa-sm'"
          @click.stop="selectMovie(movie)"
        >
          <div
            class="movie-name text-bold text-body1"
            style="line-height : 1.5rem"
          >
            {{ movie.name }}
            <rated
              v-if="movie.rating"
              :rated="movie.rating"
              :rating-reason="movie.ratingReason"
              :light-text="$q.dark.isActive"
              class="q-ma-none"
            />
          </div>
          <div
            v-if="$q.screen.gt.xs"
            class="row items-start"
          >
            <span v-if="movie.genre">{{ movie.genre }}&nbsp;&middot;&nbsp;</span>
            <span v-if="movie.duration">{{ movie.formattedDuration }}</span>
            <span v-if="showing.showingBadges.data.length">&nbsp;&middot;&nbsp;</span>
            <showing-badge
              v-for="showingBadge in showing.showingBadges.data"
              :key="showingBadge.id"
              class="q-my-none q-mx-none bg-transparent"
              :showing-badge="showingBadge"
              color="responsive"
            />
          </div>
        </div>
        <!-- eslint-disable vue/no-v-html -->
        <!-- Set internally by trusted admins -->
        <q-separator v-if="$isLandscapeView() && $q.screen.gt.xs" />
        <q-scroll-area
          v-if="$register"
          :style="{ height: $q.screen.lt.sm ? '50px' : '150px' }"
        >
          <div
            :class="$q.screen.gt.xs ? 'q-px-md' : 'q-px-sm'"
            style="line-height: 1rem;"
            v-html="movie.synopsisWithBreaks"
          />
        </q-scroll-area>
        <!-- eslint-enable vue/no-v-html -->
        <div
          class="row q-gutter-xs"
          :class="$q.screen.gt.md || $isLandscapeView() ? 'q-pa-md' : 'q-pl-sm'"
        >
          <div
            class="text-body2"
            :class="$q.screen.gt.xs ? undefined : 'col-auto text-responsive'"
          >
            {{ showingDayAndDate }}
          </div>
          <div
            class="text-responsive-grey-7 text-body2"
            :class="$q.screen.gt.xs ? undefined : 'col'"
          >
            {{ $d(showing.siteTime, 'shortTime') }}-{{ $d(showing.siteEndTime, 'shortTime') }}
          </div>
        </div>
        <q-separator v-if="$isLandscapeView() && $q.screen.gt.xs" />
        <template v-if="showAllShowtimesOptions && hasShowingsForCurrentShowingDate">
          <current-showing-date-showings
            :showing="showing"
          />
          <q-separator v-if="$isLandscapeView() && $q.screen.gt.xs" />
        </template>
        <div
          class="row q-gutter-xs"
          :class="$q.screen.gt.md || $isLandscapeView() ? 'q-pa-md' : 'q-pl-sm'"
        >
          <div
            class="text-body2"
            :class="$q.screen.gt.xs ? undefined : 'col-auto'"
          >
            {{ currentSite.displayName }}
          </div>
          <div
            class="text-responsive-grey-7 text-body2"
            :class="$q.screen.gt.xs ? undefined : 'col'"
          >
            {{ showing.screen.name }}
          </div>
        </div>
      </q-card-section>
    </component>
  </q-card>
</template>

<script>
import { date } from 'quasar'
const { addToDate } = date

import Showing from 'src/models/showing/Showing'
import ShowingBadge from 'src/modules/admin/showing-badge/components/ShowingBadge'
import CurrentShowingDateShowings from 'src/modules/checkout/components/CurrentShowingDateShowings'
import Rated from 'src/modules/consumer/components/Rated'

export default {
  name: 'MovieCard',
  components: {
    Rated,
    ShowingBadge,
    CurrentShowingDateShowings
  },
  props: {
    showing: {
      type: Showing,
      required: true
    },
    movie: {
      type: Object,
      required: true
    },
    zoom: {
      type: Number,
      required: false,
      default: 1
    },
    showAllShowtimesOptions: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  configuration: ['currentSite'],
  emits: ['movie-selected'],
  models: {
    showingsForDate: {
      movie: 'default',
      screen: 'default',
      variables () {
        return {
          date: this.currentSite.siteEffectiveDateForTime(this.showing?.time).format('YYYY-MM-DD'),
          movieId: this.showing?.movie?.id,
          siteIds: [this.currentSite.id],
          skip: !this.showing?.loaded
        }
      }
    }
  },
  computed: {
    showingDayAndDate () {
      const date = this.$d(this.showing.siteTime, 'mediumDate')
      const today = this.$d(new Date(), 'mediumDate')
      const tomorrow = this.$d(addToDate(new Date(), { days: 1 }), 'mediumDate')
      const day = date === today ? this.$i18n.t('time.today') : date === tomorrow ? this.$i18n.t('time.tomorrow') : null
      if (date === today || date === tomorrow) {
        const date = this.$d(this.showing.siteTime, 'monthDay')
        return `${day ? day + ', ' : ''}${date}`
      }
      return `${day ? day + ', ' : ''}${date}`
    },
    hasShowingsForCurrentShowingDate () {
      return _.some(this.showingsForDate?.data || [], showing => showing.validOnlinePurchaseTime && showing.reservedSeating)
    }
  },
  methods: {
    selectMovie (movie) {
      if (this.$kiosk) {
        this.$emit('movie-selected', movie.id)
        return
      }
      this.$emit('movie-selected', movie.urlSlugOrId)
    }
  }
}
</script>
